import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import moment from "moment";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { hasRole, ROLE_NAMES } from "../../Helpers/roles";
import { OrganisationsService } from "../../services/organisationsService";
import { PackService } from "../../services/packService";
import { PersonsService } from "../../services/personsService";
import { Projects } from "../../services/projectsService";
import { PageHeader } from "../components/ApplicationShell";
import Alert from "../components/Alert";
import Button from "../components/Button";
import Dialog from "../components/Dialog";
import FormField from "../components/FormField";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import Select from "../components/Select";
import StatusBadge from "../components/StatusBadge";
import { getPackStatusType } from "../components/packStatusType";
import Textarea from "../components/Textarea";
import { Toast, ToastRegion } from "../components/Toast";
import {
  canDuplicatePack,
  canEditPack,
  canExportPackRegister,
  canManagePackRegister,
  canVoidPack as userCanVoidPack,
} from "./packRegisterPermissions";
import { getPackRegisterRouteFilters } from "./packRegisterRouteFilters";

const exportColumns = [
  "Pack no.", "Status", "Start date", "End date", "Possession no.", "Worksites",
  "Marker Boards", "Level Crossings", "Week no.", "Planner", "Primary ES",
  "All ES users", "COSS users", "External COSS users", "Keywords", "Created date",
];

const formatDate = (value) => value && moment(value).isValid()
  ? moment(value).format("D MMM YYYY, HH:mm")
  : "Not specified";

const getFirstPossessionDate = (pack, field) => pack.possessions?.[0]?.[field] || pack.location?.[field];
const getLastPossessionDate = (pack, field) => pack.possessions?.[pack.possessions.length - 1]?.[field] || pack.location?.[field];
const getKeywords = (pack) => Array.from({ length: 6 }, (_, index) => pack.location?.[`keyword_${index + 1}`])
  .filter(Boolean)
  .join(", ") || "Not specified";

const getProjectLabel = (project) => [project?.number, project?.name].filter(Boolean).join(" - ");

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message || error?.data?.message || error?.message || fallback
);

// The project selection is kept for the browser session so it survives leaving the register and returning.
const getProjectFilterStorageKey = (user) => `wsm.packsRegister.projects.${user?.id || "anonymous"}`;

const readStoredProjectIds = (user) => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(getProjectFilterStorageKey(user)));
    return Array.isArray(stored) ? stored.filter((id) => typeof id === "string" && id) : [];
  } catch {
    return [];
  }
};

const storeProjectIds = (user, projectIds) => {
  try {
    if (projectIds.length > 0) window.sessionStorage.setItem(getProjectFilterStorageKey(user), JSON.stringify(projectIds));
    else window.sessionStorage.removeItem(getProjectFilterStorageKey(user));
  } catch {
    // Storage is unavailable; the selection still applies until the register is left.
  }
};

const PacksRegister = () => {
  const authUser = useSelector((state) => state.auth.user);
  const location = useLocation();
  const navigate = useNavigate();
  const isSuper = hasRole(authUser, ROLE_NAMES.SUPER_ADMIN);
  const canManage = canManagePackRegister(authUser);
  const canExport = canExportPackRegister(authUser);
  const swpOrgId = authUser?.organisation?.swp_org_id;

  const routeFilters = useMemo(
    () => getPackRegisterRouteFilters({ search: location.search, state: location.state }),
    [location.search, location.state],
  );

  const scopeFilters = useMemo(() => (!isSuper && authUser?.organisation_id
    ? { organisation_id: authUser.organisation_id }
    : {}), [authUser?.organisation_id, isSuper]);

  // A route-supplied view (e.g. a dashboard link) is shown as linked, without the remembered project selection.
  const hasRouteFilters = Object.keys(routeFilters).length > 0;
  const projectIdsRef = useRef(null);
  if (projectIdsRef.current === null) projectIdsRef.current = readStoredProjectIds(authUser);
  const restoredFilters = useMemo(() => (!hasRouteFilters && projectIdsRef.current.length > 0
    ? { project_id: projectIdsRef.current }
    : {}), [hasRouteFilters]);

  const initialFilters = useMemo(
    () => ({ ...routeFilters, ...restoredFilters, ...scopeFilters }),
    [restoredFilters, routeFilters, scopeFilters],
  );
  const visibleRouteFilters = useMemo(() => ({
    ...Object.fromEntries(
      ["attention_user_id", "status", "planner_id", "primary_es_id", "organisation_id"]
        .filter((key) => routeFilters[key] !== undefined)
        .map((key) => [key, routeFilters[key]]),
    ),
    ...restoredFilters,
  }), [restoredFilters, routeFilters]);

  const [query, setQuery] = useState({
    filters: initialFilters,
    page: 0,
    pageSize: 10,
    sortBy: "pack_reference",
    sortOrder: "desc",
  });
  const [packs, setPacks] = useState([]);
  const [pageInfo, setPageInfo] = useState({ last_page: 1, total: 0 });
  const [statuses, setStatuses] = useState([]);
  const [planners, setPlanners] = useState([]);
  const [esUsers, setEsUsers] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [voidPack, setVoidPack] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exportError, setExportError] = useState("");
  const [exportStarted, setExportStarted] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [selectedExportColumns, setSelectedExportColumns] = useState(exportColumns);

  useEffect(() => {
    setQuery((current) => {
      const next = { ...current, filters: initialFilters, page: 0 };
      return JSON.stringify(next) === JSON.stringify(current) ? current : next;
    });
  }, [initialFilters]);

  useEffect(() => {
    if (!exportStarted) return undefined;

    const timeout = setTimeout(() => setExportStarted(false), 8000);
    return () => clearTimeout(timeout);
  }, [exportStarted]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      const requests = [
        PackService.getStatus().then(setStatuses),
        PersonsService.getUsersByType([1, 2]).then((response) => setPlanners(response.data || [])),
        PersonsService.getUsersByType([3]).then((response) => setEsUsers(response.data || [])),
      ];
      if (isSuper) requests.push(OrganisationsService.getAllOrganisations().then((response) => setOrganisations(response.data || [])));
      if (swpOrgId) requests.push(Projects.getProjects(swpOrgId).then(setProjects));
      await Promise.allSettled(requests);
    };
    loadFilterOptions();
  }, [isSuper, swpOrgId]);

  const loadPacks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await PackService.getAllPacks(
        query.page,
        query.pageSize,
        query.sortBy,
        query.sortOrder,
        query.filters,
        authUser,
        isSuper,
      );
      setPacks(response?.data || []);
      setPageInfo(response || { last_page: 1, total: 0 });
    } catch (requestError) {
      setError(getErrorMessage(requestError, "The pack register could not be retrieved."));
    } finally {
      setLoading(false);
    }
  }, [authUser, isSuper, query]);

  useEffect(() => {
    const timeout = setTimeout(loadPacks, 350);
    return () => clearTimeout(timeout);
  }, [loadPacks]);

  const handleTableQuery = useCallback(({ filters, search, sortBy }) => {
    const nextFilters = { ...scopeFilters, ...filters };
    if (search) nextFilters.search = search.trim();
    if (!hasRouteFilters) {
      projectIdsRef.current = Array.isArray(filters.project_id) ? filters.project_id : [];
      storeProjectIds(authUser, projectIdsRef.current);
    }
    const sort = sortBy[0];
    setQuery((current) => {
      const next = {
        ...current,
        filters: nextFilters,
        page: 0,
        sortBy: sort?.id || null,
        sortOrder: sort ? (sort.desc ? "desc" : "asc") : null,
      };
      return JSON.stringify(next) === JSON.stringify(current) ? current : next;
    });
  }, [authUser, hasRouteFilters, scopeFilters]);

  const clearRouteFilters = useCallback(() => {
    projectIdsRef.current = [];
    storeProjectIds(authUser, []);
    if (!location.search && !location.state) return;
    navigate(location.pathname, { replace: true });
  }, [authUser, location.pathname, location.search, location.state, navigate]);

  const duplicatePack = useCallback(async (pack, edit) => {
    setWorking(true);
    setError("");
    try {
      const response = await PackService.duplicateForTemp(pack.id, edit);
      if (response?.error) throw new Error(response.error);
      navigate("/packs/create", { state: { data: response.data } });
    } catch (requestError) {
      setError(getErrorMessage(requestError, edit ? "The pack could not be opened for editing." : "The pack could not be duplicated."));
    } finally {
      setWorking(false);
    }
  }, [navigate]);

  const confirmVoid = async () => {
    if (!voidReason.trim()) return;
    setWorking(true);
    try {
      await PackService.updateStatus(voidPack.id, { status: "Void", reason: voidReason.trim() });
      setVoidPack(null);
      setVoidReason("");
      await loadPacks();
    } catch (requestError) {
      setError(getErrorMessage(requestError, "The pack could not be voided."));
    } finally {
      setWorking(false);
    }
  };

  const confirmExport = async () => {
    setWorking(true);
    setExportStarted(false);
    setExportError("");
    try {
      await PackService.exportToCsv(
        query.sortBy,
        query.sortOrder,
        query.filters,
        isSuper ? ["Organisation", ...selectedExportColumns] : selectedExportColumns,
        authUser,
        isSuper,
        { value: exportFormat },
      );
      setExportOpen(false);
      setExportStarted(true);
    } catch (requestError) {
      setExportError(getErrorMessage(requestError, "The pack export could not be started. Check your connection and try again."));
    } finally {
      setWorking(false);
    }
  };

  const columns = useMemo(() => {
    const definitions = [];

    if (isSuper) definitions.push({
      key: "organisation_id",
      header: "Organisation",
      accessor: (pack) => pack.organisation?.name || pack.organisation_name || "Not specified",
      filter: { type: "select", options: organisations.map((organisation) => ({ label: organisation.name, value: organisation.id })) },
      sortable: false,
    });

    definitions.push(
      {
        key: "pack_reference",
        header: "Reference",
        filter: { type: "text", placeholder: "Pack reference" },
        render: (pack) => <button className="wsm-data-table__record-link" onClick={() => navigate("/view/" + pack.id)} type="button">{pack.pack_reference || `WSP-${String(pack.pack_number || "").padStart(4, "0")}`}</button>,
      },
      {
        key: "planned_start_date",
        header: "Start date",
        accessor: (pack) => formatDate(getFirstPossessionDate(pack, "planned_start_date")),
        filter: { label: "Start date", type: "date" },
      },
      {
        key: "possession_no",
        header: "Possession no.",
        accessor: (pack) => pack.location?.possession_no || "Not specified",
        filter: { type: "text", placeholder: "Possession number" },
        sortable: false,
      },
      {
        key: "primary_es_id",
        header: "Primary ES",
        accessor: (pack) => pack.users?.esusers?.[0]?.name || "Not specified",
        filter: { type: "select", options: esUsers.map((user) => ({ label: user.name, value: user.id })) },
        sortable: false,
      },
      {
        key: "keywords",
        header: "Keywords",
        accessor: getKeywords,
        filter: { type: "text", placeholder: "Keywords" },
        sortable: false,
      },
      {
        key: "status",
        header: "Status",
        filter: { type: "select", options: statuses.map((status) => {
          const value = typeof status === "string" ? status : status.name || status.status;
          return { label: value, value };
        }).filter((option) => option.value) },
        render: (pack) => <StatusBadge type={getPackStatusType(pack.status)}>{pack.status || "Not specified"}</StatusBadge>,
        sortable: false,
      },
      {
        key: "planned_end_date",
        header: "End date",
        accessor: (pack) => formatDate(getLastPossessionDate(pack, "planned_end_date")),
        filter: { label: "End date", type: "date" },
        initiallyHidden: true,
      },
      {
        key: "planner_id",
        header: "Planner",
        accessor: (pack) => pack.planner_name || "Not specified",
        filter: { type: "select", options: planners.map((user) => ({ label: user.name, value: user.id })) },
        initiallyHidden: true,
        sortable: false,
      },
      {
        key: "week_no",
        header: "Week no.",
        accessor: (pack) => pack.location?.week_no || "Not specified",
        filter: { type: "text", placeholder: "Week number" },
        initiallyHidden: true,
        sortable: false,
      },
      {
        key: "project_id",
        header: "Project",
        accessor: (pack) => getProjectLabel(pack.location?.project) || "Not specified",
        filter: {
          label: "Projects",
          type: "multiselect",
          options: projects.map((project) => ({ label: getProjectLabel(project) || "Unnamed project", value: project.id })),
          placeholder: "All projects",
        },
        initiallyHidden: true,
        sortable: false,
      },
    );

    return definitions;
  }, [esUsers, isSuper, navigate, organisations, planners, projects, statuses]);

  const actions = useMemo(() => [
    { label: "View pack", onClick: (pack) => navigate("/view/" + pack.id) },
    { label: "Edit pack", loadingLabel: "Opening pack...", isVisible: (pack) => canEditPack(authUser, pack), onClick: (pack) => duplicatePack(pack, true) },
    { label: "Duplicate pack", loadingLabel: "Duplicating...", isVisible: (pack) => canDuplicatePack(authUser, pack), onClick: (pack) => duplicatePack(pack, false) },
    {
      label: "Void pack",
      isVisible: (pack) => userCanVoidPack(authUser, pack),
      onClick: (pack) => setVoidPack(pack),
    },
  ], [authUser, duplicatePack, navigate]);



  return (
    <>
      <PageHeader
        actions={canManage ? <Button icon={Plus} onClick={() => navigate("/packs/resume")}>Create pack</Button> : undefined}
        description="Below is a list of packs created within your organisation. Use the provided filters to sort through and access specific records"
        title="Packs register"
      />

      {exportStarted && (
        <ToastRegion>
          <Toast onDismiss={() => setExportStarted(false)} title="Export started" type="success">
            Your export is being prepared. Monitor its progress and download it from <Link to="/packs/exports">Data exports</Link>.
          </Toast>
        </ToastRegion>
      )}

      <ResponsiveDataTable
        actions={actions}
        caption="Worksite packs"
        columns={columns}
        data={packs}
        emptyAction={canManage ? <Button onClick={() => navigate("/packs/resume")}>Create pack</Button> : undefined}
        emptyDescription="No packs match the current search and filters."
        error={error}
        initialFilters={visibleRouteFilters}
        key={JSON.stringify(visibleRouteFilters)}
        initialSort={{ key: "pack_reference", direction: "desc" }}
        loading={loading}
        manual
        onClearFilters={clearRouteFilters}
        onExport={canExport ? () => { setExportError(""); setExportOpen(true); } : undefined}
        onPageChange={(page) => setQuery((current) => ({ ...current, page }))}
        onPageSizeChange={(pageSize) => setQuery((current) => ({ ...current, page: 0, pageSize }))}
        onQueryChange={handleTableQuery}
        onRetry={loadPacks}
        pageCount={Math.max(Number(pageInfo.last_page) || 1, 1)}
        pageIndex={query.page}
        pageSize={query.pageSize}
        pageSizeOptions={[10, 20, 50]}
        searchPlaceholder="Search reference, possession, WON item or keyword"
        showCount={false}
        showExport={canExport}
        totalRows={Number(pageInfo.total) || 0}
      />

      <Dialog
        actions={(
          <>
            <Button onClick={() => setExportOpen(false)} variant="outline">Cancel</Button>
            <Button disabled={selectedExportColumns.length === 0} loading={working} onClick={confirmExport}>Export</Button>
          </>
        )}
        description="Choose the data and file format for this export."
        onClose={() => setExportOpen(false)}
        open={exportOpen}
        size="large"
        title="Export pack data"
      >
        <div className="wsm-register-export">
          {exportError && <Alert title="Export could not be started" type="error">{exportError}</Alert>}
          <FormField id="pack-export-format" label="Format">
            {(props) => <Select {...props} onChange={(event) => setExportFormat(event.target.value)} value={exportFormat}><option value="csv">CSV</option><option value="xlsx">Excel</option></Select>}
          </FormField>
          <fieldset>
            <legend>Columns</legend>
            <label><input checked={selectedExportColumns.length === exportColumns.length} onChange={(event) => setSelectedExportColumns(event.target.checked ? exportColumns : [])} type="checkbox" /> Select all</label>
            <div>
              {exportColumns.map((column) => (
                <label key={column}><input checked={selectedExportColumns.includes(column)} onChange={(event) => setSelectedExportColumns((current) => event.target.checked ? [...current, column] : current.filter((item) => item !== column))} type="checkbox" /> {column}</label>
              ))}
            </div>
          </fieldset>
        </div>
      </Dialog>

      <Dialog
        actions={(
          <>
            <Button onClick={() => setVoidPack(null)} variant="outline">Cancel</Button>
            <Button disabled={!voidReason.trim()} loading={working} onClick={confirmVoid} variant="destructive">Void pack</Button>
          </>
        )}
        description="The pack will remain in the register and its history will record this action."
        onClose={() => setVoidPack(null)}
        open={Boolean(voidPack)}
        size="small"
        title="Void this pack?"
      >
        <FormField id="void-pack-reason" label="Reason" required>
          {(props) => <Textarea {...props} onChange={(event) => setVoidReason(event.target.value)} rows={4} value={voidReason} />}
        </FormField>
      </Dialog>
    </>
  );
};

export default PacksRegister;
